import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Plus,
  Briefcase,
  Search,
  ArrowRight,
  Clock,
  FileSearch,
  Eye,
  AlarmClock,
  FileCheck2,
  Layers,
  CloudUpload,
} from 'lucide-react';
import { supabase } from '../lib/supabase';
import { SUPPLEMENTARY_FORM_CODES_SQL } from '../lib/formCodes';
import { useMatter } from '../hooks/useMatter';
import {
  STATUS_IN_PROGRESS,
  STATUS_AWAITING_REVIEW,
  STATUS_AWAITING_FINAL_REVIEW,
  STATUS_READY_FOR_LODGEMENT,
  STATUS_LODGED,
  isArchivedStatus,
} from '../lib/matterStatus';
import { fetchDeadlines, type Deadline } from '../lib/deadlines';
import { checklistForVisa, getSponsorDocumentChecklist, hasSponsorUploadFlow } from '../lib/documentChecklist';

interface MatterRow {
  id: string;
  matter_id: string | null;
  visa_subclass: string;
  variant: string | null;
  status: string;
  updated_at: string;
  created_at: string;
  lodgement_date: string | null;
  initial_payment_date: string | null;
  assigned_staff_id: string | null;
  reviewer_staff_id: string | null;
  sponsor_contact_id: string | null;
  primary_contact: {
    id: string;
    given_names: string | null;
    family_name: string | null;
    email: string;
  } | null;
}

interface DocumentRow {
  id: string;
  matter_id: string;
  checklist_item: string | null;
  review_status: string;
  previous_version_id: string | null;
  uploader_role: string | null;
}

interface IntakeRow {
  matter_id: string;
  contact_id: string;
  status: string;
}

// Canonical pre-terminal statuses shown in the KPI strip. Imported (not
// hardcoded) so they can never drift from the lifecycle source of truth.
const KPI_STATUSES: { status: string; label: string; tone?: 'amber' | 'default' }[] = [
  { status: STATUS_IN_PROGRESS, label: 'In Progress' },
  { status: STATUS_AWAITING_REVIEW, label: 'Awaiting Review' },
  { status: STATUS_AWAITING_FINAL_REVIEW, label: "Awaiting Eli's final review", tone: 'amber' },
  { status: STATUS_READY_FOR_LODGEMENT, label: 'Ready for lodgement' },
  { status: STATUS_LODGED, label: 'Lodged with Home Affairs' },
];

const AWAITING_AMBER_DAYS = 3;
const AWAITING_RED_DAYS = 7;
const MS_PER_DAY = 24 * 60 * 60 * 1000;

function isoStartOfWeek(): Date {
  // ISO week starts Monday. Returns local-tz midnight.
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  const day = (d.getDay() + 6) % 7; // 0 = Monday
  d.setDate(d.getDate() - day);
  return d;
}

function startOfMonth(): Date {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setDate(1);
  return d;
}

function endOfWeek(): Date {
  const start = isoStartOfWeek();
  start.setDate(start.getDate() + 6);
  start.setHours(23, 59, 59, 999);
  return start;
}

function median(values: number[]): number | null {
  if (values.length === 0) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 0 ? (sorted[mid - 1] + sorted[mid]) / 2 : sorted[mid];
}

export default function StaffDashboard() {
  const { contact } = useMatter();
  const [rows, setRows] = useState<MatterRow[]>([]);
  const [docs, setDocs] = useState<DocumentRow[]>([]);
  const [intakes, setIntakes] = useState<IntakeRow[]>([]);
  const [deadlines, setDeadlines] = useState<Deadline[]>([]);
  // matter_id → ms timestamp the matter entered the awaiting-final-review state.
  const [awaitingSince, setAwaitingSince] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Drive sync queue health (A.10): queued depth + dead-letter count.
  const [driveQueue, setDriveQueue] = useState<{ queued: number; deadLetter: number } | null>(null);

  // Bootstrap: pull matters + docs + intake_forms + deadlines in parallel.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const [mRes, dRes, iRes, deads] = await Promise.all([
          supabase
            .from('matters')
            .select(
              `id, matter_id, visa_subclass, variant, status, updated_at, created_at, lodgement_date,
               initial_payment_date, assigned_staff_id, reviewer_staff_id, sponsor_contact_id,
               primary_contact:contacts!matters_primary_contact_id_fkey (id, given_names, family_name, email)`,
            )
            .is('deleted_at', null)
            .order('updated_at', { ascending: false }),
          supabase
            .from('documents')
            .select('id, matter_id, checklist_item, review_status, previous_version_id, uploader_role')
            .is('deleted_at', null),
          supabase
            .from('intake_forms')
            .select('matter_id, contact_id, status')
            .not('form_code', 'in', SUPPLEMENTARY_FORM_CODES_SQL),
          fetchDeadlines().catch(() => [] as Deadline[]),
        ]);
        if (cancelled) return;
        if (mRes.error) throw mRes.error;
        const matterRows = (mRes.data ?? []) as unknown as MatterRow[];
        setRows(matterRows);
        setDocs((dRes.data ?? []) as unknown as DocumentRow[]);
        setIntakes((iRes.data ?? []) as unknown as IntakeRow[]);
        setDeadlines(deads);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load dashboard');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Drive sync queue health (A.10). Two head-count queries: total queued and
  // dead-lettered. Best-effort — a failure (e.g. table absent pre-migration)
  // leaves the card hidden rather than erroring the dashboard.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // drive_sync_queue isn't in the generated database.types.ts yet (added
        // by migration 20260521000002). Cast around it, same pattern as other
        // recently-added DB objects in this codebase.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const from = supabase.from as any;
        const [queuedRes, deadRes] = await Promise.all([
          from('drive_sync_queue')
            .select('id', { count: 'exact', head: true })
            .eq('status', 'queued'),
          from('drive_sync_queue')
            .select('id', { count: 'exact', head: true })
            .eq('status', 'dead_letter'),
        ]);
        if (cancelled) return;
        if (queuedRes.error || deadRes.error) return;
        setDriveQueue({
          queued: queuedRes.count ?? 0,
          deadLetter: deadRes.count ?? 0,
        });
      } catch {
        /* leave card hidden */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Once we know which matters are at awaiting-final-review, pull the audit_log
  // transition rows so we can age them. Same source the notify-eli-cadence
  // function uses (occurred_at of the most recent UPDATE row whose changes->after->>status
  // equals the awaiting-final-review string).
  useEffect(() => {
    let cancelled = false;
    const awaiting = rows.filter((r) => r.status === STATUS_AWAITING_FINAL_REVIEW);
    if (awaiting.length === 0) {
      setAwaitingSince({});
      return;
    }
    (async () => {
      const ids = awaiting.map((r) => r.id);
      const { data } = await supabase
        .from('audit_log')
        .select('row_id, occurred_at')
        .eq('table_name', 'matters')
        .eq('action', 'update')
        .in('row_id', ids)
        .filter('changes->after->>status', 'eq', STATUS_AWAITING_FINAL_REVIEW)
        .order('occurred_at', { ascending: false });
      if (cancelled) return;
      const out: Record<string, number> = {};
      for (const row of (data ?? []) as Array<{ row_id: string; occurred_at: string }>) {
        // First (most recent) row wins per matter.
        if (!(row.row_id in out)) out[row.row_id] = new Date(row.occurred_at).getTime();
      }
      setAwaitingSince(out);
    })();
    return () => {
      cancelled = true;
    };
  }, [rows]);

  // Active (pre-terminal) matters only — terminal matters live on /matters/archived.
  const activeMatters = useMemo(
    () => rows.filter((r) => !isArchivedStatus(r.status)),
    [rows],
  );

  // Per-matter doc roll-ups keyed by matter id (active versions only).
  const docsByMatter = useMemo(() => {
    const supersededIds = new Set<string>();
    for (const d of docs) if (d.previous_version_id) supersededIds.add(d.previous_version_id);
    const map = new Map<string, DocumentRow[]>();
    for (const d of docs) {
      if (supersededIds.has(d.id)) continue;
      const arr = map.get(d.matter_id) ?? [];
      arr.push(d);
      map.set(d.matter_id, arr);
    }
    return map;
  }, [docs]);

  // Most-recent intake per (matter, contact). Used for both applicant intake
  // (incompleteness signal) and sponsor checklist progress later.
  const intakeByMatterContact = useMemo(() => {
    const map = new Map<string, string>();
    for (const i of intakes) {
      // Latest wins by insertion order (we don't sort here — first-write
      // semantics fine; intake_forms.status only progresses forward).
      map.set(`${i.matter_id}:${i.contact_id}`, i.status);
    }
    return map;
  }, [intakes]);

  // KPI counts by canonical status.
  const kpiCounts = useMemo(() => {
    const out: Record<string, number> = {};
    for (const k of KPI_STATUSES) out[k.status] = 0;
    for (const r of activeMatters) {
      if (r.status in out) out[r.status]++;
    }
    return out;
  }, [activeMatters]);

  // ─────────────────────────────────────────────────────────────────────
  // Action queue: docs awaiting MY review · matters awaiting my Eli-side
  // review (with age coloring) · deadlines this week.
  // ─────────────────────────────────────────────────────────────────────
  const myDocsToReview = useMemo(() => {
    if (!contact?.id) return 0;
    const myMatterIds = new Set(
      activeMatters.filter((r) => r.assigned_staff_id === contact.id).map((r) => r.id),
    );
    if (myMatterIds.size === 0) return 0;
    let n = 0;
    for (const d of docs) {
      if (!myMatterIds.has(d.matter_id)) continue;
      if (d.review_status === 'pending' || d.review_status === 'pending_review') n++;
    }
    return n;
  }, [docs, activeMatters, contact?.id]);

  const myAwaitingFinalReview = useMemo(() => {
    if (!contact?.id) return { count: 0, oldestDays: 0 };
    const mine = activeMatters.filter(
      (r) => r.status === STATUS_AWAITING_FINAL_REVIEW && r.reviewer_staff_id === contact.id,
    );
    let oldestDays = 0;
    for (const m of mine) {
      const since = awaitingSince[m.id];
      if (!since) continue;
      const days = (Date.now() - since) / MS_PER_DAY;
      if (days > oldestDays) oldestDays = days;
    }
    return { count: mine.length, oldestDays };
  }, [activeMatters, contact?.id, awaitingSince]);

  const deadlinesThisWeek = useMemo(() => {
    const end = endOfWeek().getTime();
    return deadlines.filter((d) => {
      const dt = new Date(d.date).getTime();
      return dt <= end;
    }).length;
  }, [deadlines]);

  // ─────────────────────────────────────────────────────────────────────
  // Throughput (live; no materialised view in V1).
  // ─────────────────────────────────────────────────────────────────────
  const throughput = useMemo(() => {
    const startWeek = isoStartOfWeek().getTime();
    const startMonth = startOfMonth().getTime();
    let lodgedThisWeek = 0;
    let lodgedThisMonth = 0;
    const cycleDays: number[] = [];
    const ninetyDaysAgo = Date.now() - 90 * MS_PER_DAY;
    for (const r of rows) {
      if (!r.lodgement_date) continue;
      const lodgedMs = new Date(r.lodgement_date).getTime();
      if (lodgedMs >= startWeek) lodgedThisWeek++;
      if (lodgedMs >= startMonth) lodgedThisMonth++;
      if (lodgedMs >= ninetyDaysAgo && r.created_at) {
        const created = new Date(r.created_at).getTime();
        if (lodgedMs >= created) {
          cycleDays.push((lodgedMs - created) / MS_PER_DAY);
        }
      }
    }
    return {
      lodgedThisWeek,
      lodgedThisMonth,
      medianCycleDays: cycleDays.length >= 5 ? Math.round(median(cycleDays) ?? 0) : null,
    };
  }, [rows]);

  // ─────────────────────────────────────────────────────────────────────
  // Blocked breakdown — signals (not click-throughs).
  // ─────────────────────────────────────────────────────────────────────
  const blocked = useMemo(() => {
    let intakeIncomplete = 0;
    let docsMissing = 0;
    let paymentOutstanding = 0;
    for (const r of activeMatters) {
      // Intake incomplete: applicant intake_form NOT in (completed, submitted).
      // Applicant id resolved via the FK-joined primary_contact (contacts.id).
      const applicantId = r.primary_contact?.id;
      const intakeStatus = applicantId
        ? intakeByMatterContact.get(`${r.id}:${applicantId}`)
        : undefined;
      if (intakeStatus !== 'completed' && intakeStatus !== 'submitted') intakeIncomplete++;

      // Docs missing: required > uploaded.
      const checklist = checklistForVisa(r.visa_subclass, r.variant);
      const required = checklist.filter((c) => c.required);
      const matterDocs = docsByMatter.get(r.id) ?? [];
      const uploadedKeys = new Set(
        matterDocs.filter((d) => d.checklist_item).map((d) => d.checklist_item as string),
      );
      const uploaded = required.filter((c) => uploadedKeys.has(c.key)).length;
      if (uploaded < required.length) docsMissing++;

      // Payment outstanding: initial_payment_date is null.
      if (!r.initial_payment_date) paymentOutstanding++;
    }

    // Staff side.
    let docsToReview = 0;
    for (const d of docs) {
      if (d.review_status === 'pending' || d.review_status === 'pending_review') docsToReview++;
    }
    let awaitingEliOver3d = 0;
    for (const r of activeMatters) {
      if (r.status !== STATUS_AWAITING_FINAL_REVIEW) continue;
      const since = awaitingSince[r.id];
      if (!since) continue;
      if ((Date.now() - since) / MS_PER_DAY > 3) awaitingEliOver3d++;
    }
    let sponsorDocsMissing = 0;
    for (const r of activeMatters) {
      if (!r.sponsor_contact_id) continue;
      if (!hasSponsorUploadFlow(r.visa_subclass)) continue;
      const required = getSponsorDocumentChecklist(r.visa_subclass).filter((c) => c.required);
      if (required.length === 0) continue;
      const matterDocs = docsByMatter.get(r.id) ?? [];
      // Sponsor uploads are tagged uploader_role='sponsor' (per
      // 20260508000002_sponsor_documents.sql); fall back to checklist_item match.
      const sponsorKeys = new Set(
        matterDocs
          .filter((d) => d.uploader_role === 'sponsor' && d.checklist_item)
          .map((d) => d.checklist_item as string),
      );
      const uploaded = required.filter((c) => sponsorKeys.has(c.key)).length;
      if (uploaded < required.length) sponsorDocsMissing++;
    }
    return {
      intakeIncomplete,
      docsMissing,
      paymentOutstanding,
      docsToReview,
      awaitingEliOver3d,
      sponsorDocsMissing,
    };
  }, [activeMatters, docsByMatter, docs, intakeByMatterContact, awaitingSince]);

  // ─────────────────────────────────────────────────────────────────────
  // Recently active — deduped by matter, top 6.
  // ─────────────────────────────────────────────────────────────────────
  const recentlyActive = useMemo(() => {
    const cutoff = Date.now() - 7 * 24 * 60 * 60 * 1000;
    const seen = new Set<string>();
    const out: MatterRow[] = [];
    for (const r of rows) {
      if (seen.has(r.id)) continue;
      if (new Date(r.updated_at).getTime() < cutoff) continue;
      seen.add(r.id);
      out.push(r);
      if (out.length >= 6) break;
    }
    return out;
  }, [rows]);

  const firstName = contact?.given_names?.trim() || 'there';
  const role = contact?.staff_role ?? 'staff';
  const isAdmin = role === 'admin';

  if (loading) {
    return <div className="text-sm text-slate-500">Loading your dashboard...</div>;
  }
  if (error) {
    return <div className="card p-6 text-sm text-red-600">Couldn't load: {error}</div>;
  }

  const hasAttention =
    myDocsToReview > 0 ||
    myAwaitingFinalReview.count > 0 ||
    deadlinesThisWeek > 0;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-6">
      {/* MAIN COLUMN */}
      <div className="space-y-6">
        {/* Title row */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
          <div className="min-w-0">
            <div className="text-xs text-slate-500 mb-1">Hi {firstName}.</div>
            <h1 className="text-2xl font-semibold text-[#0B2341]">
              {hasAttention ? 'What needs your attention today' : 'All caught up'}
            </h1>
            {isAdmin && (
              <div className="mt-2">
                <Link
                  to="/matters?status=all-incl-archived"
                  className="text-xs text-[#2CABEA] hover:underline inline-flex items-center gap-1"
                >
                  <Layers size={12} /> View all matter statuses (active + archived)
                </Link>
              </div>
            )}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {isAdmin && (
              <Link
                to="/matters/new"
                className="btn-primary inline-flex items-center gap-1.5 !py-2 !px-3 text-sm"
              >
                <Plus size={14} /> New matter
              </Link>
            )}
            <Link
              to="/matters"
              className="inline-flex items-center gap-1.5 text-sm text-[#2CABEA] hover:underline"
            >
              <Briefcase size={14} /> All matters
            </Link>
          </div>
        </div>

        {/* Needs your attention — three tiles. */}
        <section>
          <h2 className="text-xs uppercase tracking-wide text-slate-500 font-semibold mb-2">
            Needs your attention
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <ActionTile
              icon={<FileCheck2 size={16} />}
              label="Documents waiting for my review"
              count={myDocsToReview}
              to="/matters?filter=mine"
              tone={myDocsToReview > 0 ? 'amber' : 'default'}
            />
            <ActionTile
              icon={<Eye size={16} />}
              label="Matters awaiting your final review"
              count={myAwaitingFinalReview.count}
              to={`/matters?status=${encodeURIComponent(STATUS_AWAITING_FINAL_REVIEW)}&filter=mine`}
              tone={
                myAwaitingFinalReview.oldestDays > AWAITING_RED_DAYS
                  ? 'red'
                  : myAwaitingFinalReview.oldestDays > AWAITING_AMBER_DAYS
                  ? 'amber'
                  : 'default'
              }
              footnote={
                myAwaitingFinalReview.count > 0
                  ? `Oldest: ${Math.floor(myAwaitingFinalReview.oldestDays)}d`
                  : null
              }
            />
            <ActionTile
              icon={<AlarmClock size={16} />}
              label="Deadlines this week"
              count={deadlinesThisWeek}
              to="/deadlines"
              tone={deadlinesThisWeek > 0 ? 'amber' : 'default'}
            />
          </div>
        </section>

        {/* Lifecycle status strip — canonical statuses, click-through to filter. */}
        <section>
          <h2 className="text-xs uppercase tracking-wide text-slate-500 font-semibold mb-2">
            Lifecycle
          </h2>
          <div className="card p-0 overflow-hidden">
            <div className="grid grid-cols-2 sm:grid-cols-5 sm:divide-x divide-slate-100 divide-y sm:divide-y-0">
              {KPI_STATUSES.map((k) => (
                <StatPill
                  key={k.status}
                  label={k.label}
                  value={kpiCounts[k.status] ?? 0}
                  status={k.status}
                  tone={k.tone}
                />
              ))}
            </div>
          </div>
        </section>

        {/* Throughput */}
        <section>
          <h2 className="text-xs uppercase tracking-wide text-slate-500 font-semibold mb-2">
            Throughput
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <ThroughputTile label="Lodged this week" value={throughput.lodgedThisWeek.toString()} />
            <ThroughputTile label="Lodged this month" value={throughput.lodgedThisMonth.toString()} />
            <ThroughputTile
              label="Median days contract → lodgement (90d)"
              value={
                throughput.medianCycleDays != null
                  ? `${throughput.medianCycleDays}d`
                  : '-'
              }
              hint={
                throughput.medianCycleDays == null
                  ? 'Need ≥5 lodged matters in window'
                  : null
              }
            />
          </div>
        </section>

        {/* Blocked breakdown */}
        <section>
          <h2 className="text-xs uppercase tracking-wide text-slate-500 font-semibold mb-2">
            Where matters are stuck
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <BlockedColumn
              title="Blocked on client"
              items={[
                { label: 'Intake incomplete', count: blocked.intakeIncomplete },
                { label: 'Docs missing', count: blocked.docsMissing },
                { label: 'Payment outstanding', count: blocked.paymentOutstanding },
              ]}
            />
            <BlockedColumn
              title="Blocked on staff"
              items={[
                { label: 'Docs to review', count: blocked.docsToReview },
                { label: 'Awaiting Eli > 3 days', count: blocked.awaitingEliOver3d },
                { label: 'Sponsor docs missing', count: blocked.sponsorDocsMissing },
              ]}
            />
          </div>
        </section>
      </div>

      {/* RIGHT RAIL */}
      <div className="space-y-4">
        <div className="card p-4">
          <h3 className="text-xs uppercase tracking-wide text-slate-500 font-semibold mb-3">
            Quick links
          </h3>
          <ul className="space-y-2 text-sm">
            {isAdmin && (
              <li>
                <Link
                  to="/admin/forms-review"
                  className="text-[#2CABEA] hover:underline inline-flex items-center gap-1.5"
                >
                  <FileSearch size={13} /> Forms catalogue
                </Link>
              </li>
            )}
            <li>
              <Link
                to="/matters"
                className="text-[#2CABEA] hover:underline inline-flex items-center gap-1.5"
              >
                <Search size={13} /> Search all matters
              </Link>
            </li>
            {isAdmin && (
              <li>
                <Link
                  to="/matters/new"
                  className="text-[#2CABEA] hover:underline inline-flex items-center gap-1.5"
                >
                  <Plus size={13} /> Create new matter
                </Link>
              </li>
            )}
          </ul>
        </div>

        {driveQueue && (driveQueue.queued > 0 || driveQueue.deadLetter > 0) && (
          <div
            className={`card p-4 border ${
              driveQueue.deadLetter > 0 ? 'bg-amber-50 border-amber-200' : 'border-slate-100'
            }`}
          >
            <h3 className="text-xs uppercase tracking-wide text-slate-500 font-semibold mb-3 inline-flex items-center gap-1.5">
              <CloudUpload size={13} /> Drive sync
            </h3>
            <div className="flex items-center justify-between text-sm">
              <span className="text-slate-600">Queued</span>
              <span className="tabular-nums font-semibold text-[#0B2341]">{driveQueue.queued}</span>
            </div>
            <div className="flex items-center justify-between text-sm mt-1">
              <span className="text-slate-600">Dead-letter</span>
              <span
                className={`tabular-nums font-semibold ${
                  driveQueue.deadLetter > 0 ? 'text-amber-700' : 'text-slate-400'
                }`}
              >
                {driveQueue.deadLetter}
              </span>
            </div>
            {driveQueue.deadLetter > 0 && (
              <p className="text-[11px] text-amber-700 mt-2">
                Some documents failed to mirror to Drive after repeated retries. Ask Tzvi to check the queue.
              </p>
            )}
          </div>
        )}

        <div className="card p-4">
          <h3 className="text-xs uppercase tracking-wide text-slate-500 font-semibold mb-3">
            Recently active
          </h3>
          {recentlyActive.length === 0 ? (
            <div className="text-xs text-slate-500">No movement in the last 7 days.</div>
          ) : (
            <ul className="space-y-2.5 text-sm">
              {recentlyActive.map((m) => (
                <RailMatterRow key={m.id} matter={m} />
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

function ActionTile({
  icon,
  label,
  count,
  to,
  tone = 'default',
  footnote = null,
}: {
  icon: React.ReactNode;
  label: string;
  count: number;
  to: string;
  tone?: 'default' | 'amber' | 'red';
  footnote?: string | null;
}) {
  const toneCls =
    count === 0
      ? 'text-slate-700 bg-white'
      : tone === 'red'
      ? 'text-red-700 bg-red-50 border-red-200'
      : tone === 'amber'
      ? 'text-amber-800 bg-amber-50 border-amber-200'
      : 'text-[#0B2341] bg-white';
  return (
    <Link
      to={to}
      className={`card p-4 hover:shadow-sm transition-shadow border ${toneCls}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="text-xs font-medium opacity-80">{label}</div>
        <div className="opacity-70 shrink-0">{icon}</div>
      </div>
      <div className="mt-1 flex items-baseline gap-2">
        <div className="text-2xl font-bold tabular-nums">{count}</div>
        {footnote && <div className="text-xs opacity-70">{footnote}</div>}
      </div>
    </Link>
  );
}

function StatPill({
  label,
  value,
  status,
  tone = 'default',
}: {
  label: string;
  value: number;
  status?: string;
  tone?: 'default' | 'amber' | 'red';
}) {
  const valueColour =
    value > 0 && tone === 'red'
      ? 'text-red-600'
      : value > 0 && tone === 'amber'
      ? 'text-amber-600'
      : 'text-[#0B2341]';
  const inner = (
    <>
      <div className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold leading-tight">
        {label}
      </div>
      <div className={`text-xl font-bold leading-tight mt-1 ${valueColour}`}>{value}</div>
    </>
  );
  if (status) {
    return (
      <Link
        to={`/matters?status=${encodeURIComponent(status)}`}
        className="px-3 py-2.5 hover:bg-slate-50 transition-colors block text-left"
      >
        {inner}
      </Link>
    );
  }
  return <div className="px-3 py-2.5">{inner}</div>;
}

function ThroughputTile({
  label,
  value,
  hint = null,
}: {
  label: string;
  value: string;
  hint?: string | null;
}) {
  return (
    <div className="card p-4">
      <div className="text-xs text-slate-500 font-medium">{label}</div>
      <div className="text-2xl font-bold text-[#0B2341] mt-1 tabular-nums">{value}</div>
      {hint && <div className="text-[10px] text-slate-400 mt-0.5">{hint}</div>}
    </div>
  );
}

function BlockedColumn({
  title,
  items,
}: {
  title: string;
  items: { label: string; count: number }[];
}) {
  const total = items.reduce((s, i) => s + i.count, 0);
  return (
    <div className="card p-4">
      <div className="flex items-baseline justify-between mb-3">
        <h3 className="text-sm font-semibold text-[#0B2341]">{title}</h3>
        <span className="text-xs text-slate-500">{total} matter{total === 1 ? '' : 's'}</span>
      </div>
      <ul className="space-y-1.5 text-sm">
        {items.map((it) => (
          <li key={it.label} className="flex items-center justify-between">
            <span className="text-slate-600">{it.label}</span>
            <span className={`tabular-nums font-semibold ${it.count > 0 ? 'text-[#0B2341]' : 'text-slate-400'}`}>
              {it.count}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function RailMatterRow({ matter }: { matter: MatterRow }) {
  const navigate = useNavigate();
  const clientName =
    [matter.primary_contact?.given_names, matter.primary_contact?.family_name]
      .filter(Boolean)
      .join(' ')
      .trim() ||
    matter.primary_contact?.email ||
    'Unnamed';
  const visaLabel = `${matter.visa_subclass}${matter.variant ? ` ${matter.variant}` : ''}`;
  const updatedAgo = relativeTime(matter.updated_at);
  return (
    <li>
      <div
        role="button"
        tabIndex={0}
        onClick={() => navigate(`/matters/${matter.id}`)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            navigate(`/matters/${matter.id}`);
          }
        }}
        className="block group cursor-pointer"
      >
        {matter.primary_contact?.id ? (
          <Link
            to={`/contacts/${matter.primary_contact.id}`}
            onClick={(e) => e.stopPropagation()}
            className="font-medium text-[#0B2341] truncate text-sm hover:text-[#2CABEA] hover:underline block"
          >
            {clientName}
          </Link>
        ) : (
          <div className="font-medium text-[#0B2341] truncate text-sm">{clientName}</div>
        )}
        <div className="text-xs text-slate-500 truncate group-hover:text-[#2CABEA] flex items-center gap-1">
          <Clock size={10} /> {visaLabel} · {updatedAgo}
          <ArrowRight size={11} className="ml-auto text-slate-300" />
        </div>
      </div>
    </li>
  );
}

function relativeTime(iso: string): string {
  const then = new Date(iso).getTime();
  const now = Date.now();
  const diffMin = Math.round((now - then) / 60000);
  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.round(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.round(diffHr / 24);
  if (diffDay < 30) return `${diffDay}d ago`;
  return new Date(iso).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' });
}
